// Pure helpers for building/updating a mapping field. Kept outside MappingPage so the page's
// state logic reads as a sequence of calls to named, independently testable functions.
import {
  AUTOFILL_OWNER,
  DEFAULT_FIELD_HEIGHT_PCT,
  DEFAULT_FIELD_OWNER,
  DEFAULT_FIELD_TYPE,
  DEFAULT_FIELD_WIDTH_PCT,
  DEFAULT_FIELD_X_PCT,
  DEFAULT_FIELD_Y_PCT,
} from "./mappingConstants";

/**
 * Smallest unused `fieldN` name. Plain `fields.length + 1` collides once a field has been
 * deleted from the middle of the list. The next add would reuse an existing field's name and
 * silently merge into it instead of creating a new one.
 */
const nextFieldName = (fields) => {
  const taken = new Set(fields.map((field) => field.name));
  let index = fields.length + 1;
  while (taken.has(`field${index}`)) {
    index += 1;
  }
  return `field${index}`;
};

/** A freshly-added field: a small default box on the active page, unlinked, until the admin
 * draws its real rectangle and/or links it to a prefill key. */
export const createMappingField = (fields, pageNumber) => ({
  name: nextFieldName(fields),
  label: `Field ${fields.length + 1}`,
  fieldType: DEFAULT_FIELD_TYPE,
  signerRole: DEFAULT_FIELD_OWNER,
  pageNumber,
  xPct: DEFAULT_FIELD_X_PCT,
  yPct: DEFAULT_FIELD_Y_PCT,
  widthPct: DEFAULT_FIELD_WIDTH_PCT,
  heightPct: DEFAULT_FIELD_HEIGHT_PCT,
  required: false,
  prefillKey: null,
  sortOrder: fields.length,
});

/**
 * Linking a field to a prefill key hands both "who fills it" and its type over to the catalog.
 * A linked field is always AUTOFILLED, and its type follows the key so e.g. `client.email` can't
 * end up mapped as a plain TEXT box. Unlinking hands both back to a plain client-fillable field.
 */
export const applyPrefillKeyToField = (field, prefillKey, prefillByKey) => {
  if (!prefillKey) {
    return { ...field, prefillKey: null, signerRole: DEFAULT_FIELD_OWNER };
  }
  const catalogEntry = prefillByKey[prefillKey];
  return {
    ...field,
    prefillKey,
    signerRole: AUTOFILL_OWNER,
    fieldType: catalogEntry?.fieldType || DEFAULT_FIELD_TYPE,
  };
};

/** Renumbers sortOrder to match on-screen order before saving. Additions and deletions can
 * otherwise leave gaps or duplicate values that mean nothing to the backend. */
export const withSequentialSortOrder = (fields) =>
  fields.map((field, index) => ({ ...field, sortOrder: index }));
